import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, Search, Rocket, Undo2, History, ArrowUpDown, ArrowUp, ArrowDown, Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import {
  fetchDeviceStockPaged,
  createDeviceStock,
  updateDeviceStock,
  deleteDeviceStock,
  deployDeviceStock,
  returnDeviceStock,
  fetchCustomers,
  fetchDeviceStockHistory,
  fetchSimCards,
  createSimCard,
  DeviceStock,
} from "@/lib/reseller-api";
import { fetchDevices, fetchPermissionsMap, userDevicesFromMap, updateDevice, linkDevice } from "@/lib/traccar-api";
import { AddVehicleDialog } from "@/components/devices/AddVehicleDialog";

const MANUFACTURER_MODELS: Record<string, string[]> = {
  AOVX: ["VL100", "VL300"],
  Coban: ["BN303"],
  Concox: ["GT06N", "JM-BL10", "JM-VL01", "JM-VL02", "JM-VL03", "JM-VL04", "JM-VL05", "WeTrack2"],
  Globalstar: ["AtlasTrax"],
  Mobilogix: ["MT2000"],
  Ruptela: ["FM-Eco4", "FM-Eco4+", "FM-Pro4", "HCV5"],
  Suntech: ["ST300", "ST310", "ST340", "ST600"],
  X3Tech: ["NT20", "NT40"],
};

const MANUFACTURERS = Object.keys(MANUFACTURER_MODELS);

const statusColors: Record<DeviceStock["status"], string> = {
  in_stock: "bg-green-500",
  deployed: "bg-blue-500",
  returned: "bg-yellow-500",
  maintenance: "bg-orange-500",
  retired: "bg-gray-500",
};

export default function DeviceStockPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState(() => searchParams.get("imei") ?? "");
  const [search, setSearch] = useState(() => searchParams.get("imei") ?? "");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [isOpen, setIsOpen] = useState(false);
  const [isDeployOpen, setIsDeployOpen] = useState(false);
  const [isReturnOpen, setIsReturnOpen] = useState(false);
  const [isReturning, setIsReturning] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<DeviceStock | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<DeviceStock | null>(null);
  const [historyDevice, setHistoryDevice] = useState<DeviceStock | null>(null);
  const [deployCustomerId, setDeployCustomerId] = useState<string>("");
  const [deployTraccarDeviceId, setDeployTraccarDeviceId] = useState<string>("");
  const [deployCustomerOpen, setDeployCustomerOpen] = useState(false);
  const [deployVehicleOpen, setDeployVehicleOpen] = useState(false);
  const [isAddVehicleFromDeploy, setIsAddVehicleFromDeploy] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [selectedManufacturer, setSelectedManufacturer] = useState<string>("");
  const [selectedModel, setSelectedModel] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<string>("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [simOpen, setSimOpen] = useState(false);
  const [selectedSimId, setSelectedSimId] = useState<string>("none");
  const [createNewSim, setCreateNewSim] = useState(false);
  const [newSimIccid, setNewSimIccid] = useState("");
  const [newSimPhone, setNewSimPhone] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: fetchCustomers,
  });

  const { data: traccarDevices = [] } = useQuery({
    queryKey: ["devices"],
    queryFn: fetchDevices,
    staleTime: 60000,
  });

  const traccarDeviceMap = useMemo(() => {
    const map = new Map<number, string>();
    traccarDevices.forEach(d => map.set(d.id, d.name));
    return map;
  }, [traccarDevices]);

  const matchingTraccarIds = useMemo(() => {
    if (!search) return undefined;
    const lower = search.toLowerCase();
    const ids = traccarDevices.filter(d => d.name.toLowerCase().includes(lower)).map(d => d.id);
    return ids.length > 0 ? ids : undefined;
  }, [search, traccarDevices]);

  const { data: pagedResult, isLoading } = useQuery({
    queryKey: ["device-stock", "paged", page, search, statusFilter, sortBy, sortDir, matchingTraccarIds],
    queryFn: () => fetchDeviceStockPaged({ page, limit: PAGE_SIZE, search, status: statusFilter, sortBy, sortDir, traccarDeviceIds: matchingTraccarIds }),
  });
  const devices = pagedResult?.data ?? [];
  const total = pagedResult?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const { data: simCards = [] } = useQuery({
    queryKey: ["sim-cards"],
    queryFn: fetchSimCards,
  });

  // Get the selected customer's traccar_user_id
  const selectedCustomer = customers.find((c) => c.id === deployCustomerId);

  const { data: permsMap } = useQuery({
    queryKey: ["permissions-map"],
    queryFn: fetchPermissionsMap,
    enabled: !!selectedCustomer?.traccar_user_id,
  });

  const customerVehicles = (selectedCustomer?.traccar_user_id && permsMap)
    ? userDevicesFromMap(selectedCustomer.traccar_user_id, traccarDevices, permsMap)
    : [];

  const { data: history = [], isLoading: historyLoading } = useQuery({
    queryKey: ["device-stock-history", historyDevice?.id],
    queryFn: () => fetchDeviceStockHistory(historyDevice!.id),
    enabled: !!historyDevice,
  });

  const createMutation = useMutation({
    mutationFn: createDeviceStock,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["device-stock"] });
      setIsOpen(false);
      toast({ title: t("reseller.deviceStock.created") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<DeviceStock> }) => updateDeviceStock(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["device-stock"] });
      setIsOpen(false);
      setEditingDevice(null);
      toast({ title: t("reseller.deviceStock.updated") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteDeviceStock,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["device-stock"] });
      toast({ title: t("reseller.deviceStock.deleted") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState<{ done: number; total: number } | null>(null);

  useEffect(() => {
    if (!bulkDeleting) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [bulkDeleting]);

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    if (!confirm(t("reseller.deviceStock.bulkDeleteConfirm", { count: ids.length }))) return;
    setBulkDeleting(true);
    setBulkDeleteProgress({ done: 0, total: ids.length });
    try {
      for (let i = 0; i < ids.length; i++) {
        await deleteDeviceStock(ids[i]);
        setBulkDeleteProgress({ done: i + 1, total: ids.length });
      }
      queryClient.invalidateQueries({ queryKey: ["device-stock"] });
      setSelectedIds(new Set());
      toast({ title: t("reseller.deviceStock.bulkDeleted", { count: ids.length }) });
    } catch (err) {
      toast({ title: t("common.error"), description: (err as Error).message, variant: "destructive" });
    } finally {
      setBulkDeleting(false);
      setBulkDeleteProgress(null);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === devices.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(devices.map((d) => d.id)));
    }
  };

  const deployMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { traccar_device_id: number; customer_id: string; vehicle_id?: string } }) =>
      deployDeviceStock(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["device-stock"] });
      setIsDeployOpen(false);
      setSelectedDevice(null);
      setIsDeploying(false);
      toast({ title: t("reseller.deviceStock.deployed") });
    },
    onError: (error: Error) => {
      setIsDeploying(false);
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const returnMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data?: { return_reason?: string } }) => returnDeviceStock(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["device-stock"]}).then();
      setIsReturnOpen(false);
      setSelectedDevice(null);
      toast({ title: t("reseller.deviceStock.returned") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });


  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedManufacturer) {
      toast({ title: t("common.error"), description: t("reseller.deviceStock.manufacturerRequired"), variant: "destructive" });
      return;
    }
    if (!selectedModel) {
      toast({ title: t("common.error"), description: t("reseller.deviceStock.modelRequired"), variant: "destructive" });
      return;
    }
    if (createNewSim && !newSimIccid.trim()) {
      toast({ title: t("common.error"), description: "ICCID is required for new SIM card", variant: "destructive" });
      return;
    }

    let simCardId: string | null = selectedSimId === "none" ? null : selectedSimId || null;

    if (createNewSim && newSimIccid.trim()) {
      try {
        const newSim = await createSimCard({ iccid: newSimIccid.trim(), ...(newSimPhone.trim() && { phone_number: newSimPhone.trim() }), status: "active" });
        simCardId = newSim.id;
        queryClient.invalidateQueries({ queryKey: ["sim-cards"] });
      } catch (err) {
        toast({ title: t("common.error"), description: (err as Error).message, variant: "destructive" });
        return;
      }
    }

    const formData = new FormData(e.currentTarget);
    const data = {
      serial_number: (formData.get("serial_number") as string) || null,
      imei: (formData.get("imei") as string) || null,
      model: selectedModel,
      manufacturer: selectedManufacturer,
      sim_card_id: simCardId,
      supplier: (formData.get("supplier") as string) || null,
      purchase_date: (formData.get("purchase_date") as string) || null,
      purchase_price: formData.get("purchase_price") ? parseFloat(formData.get("purchase_price") as string) : null,
      warranty_expiry: (formData.get("warranty_expiry") as string) || null,
      status: formData.get("status") as DeviceStock["status"],
      notes: (formData.get("notes") as string) || null,
    };

    if (editingDevice) {
      updateMutation.mutate({ id: editingDevice.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDeploySubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedDevice || !deployCustomerId || !deployTraccarDeviceId) return;
    setIsDeploying(true);
    try {
      const traccarDeviceId = parseInt(deployTraccarDeviceId);
      const vehicle = customerVehicles.find((v) => v.id === traccarDeviceId);
      if (vehicle) {
        const updates: typeof vehicle = { ...vehicle };
        updates.uniqueId = selectedDevice.imei;
        updates.phone = selectedDevice.sim_card?.phone_number;
        updates.model = selectedDevice.model;
        await updateDevice(updates);
      }
      const data = {
        traccar_device_id: traccarDeviceId,
        customer_id: deployCustomerId,
      };
      deployMutation.mutate({ id: selectedDevice.id, data });
    } catch {
      setIsDeploying(false);
    }
  };

  const resetDeployState = () => {
    setSelectedDevice(null);
    setDeployCustomerId("");
    setDeployTraccarDeviceId("");
    setDeployCustomerOpen(false);
    setDeployVehicleOpen(false);
    setIsAddVehicleFromDeploy(false);
    setIsDeploying(false);
  };

  const handleReturnSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedDevice) return;
    const formData = new FormData(e.currentTarget);
    const data = {
      return_reason: (formData.get("return_reason") as string) || undefined,
    };
    setIsReturning(true);
    try {
      if (selectedDevice.traccar_device_id) {
        const allDevices = await fetchDevices();
        const vehicle = allDevices.find((d) => d.id === selectedDevice.traccar_device_id);
        if (vehicle) {
          const updates = { ...vehicle, uniqueId: `not_installed_${crypto.randomUUID()}`, phone: "", model: "" };
          await updateDevice(updates).catch(console.error);
        }
      }
      returnMutation.mutate({ id: selectedDevice.id, data });
    } finally {
      setIsReturning(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {bulkDeleting && (
          <div className="bg-destructive/10 border border-destructive rounded-lg px-4 py-3 flex items-center gap-3">
            <span className="text-lg font-semibold text-destructive animate-pulse">{t("common.doNotLeave")}</span>
            <span className="text-sm text-destructive">{bulkDeleteProgress ? `${bulkDeleteProgress.done} / ${bulkDeleteProgress.total}` : ""}</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">
            {t("reseller.deviceStock.title")}
            {!isLoading && <span className="text-muted-foreground text-xl font-normal ml-2">{t("reseller.deviceStock.count", { count: total })}</span>}
          </h1>
          <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) { setEditingDevice(null); setSelectedManufacturer(""); setSelectedModel(""); setSelectedSimId("none"); setCreateNewSim(false); setNewSimIccid(""); setNewSimPhone(""); } }}>
            <DialogTrigger asChild>
              <Button><Plus className="h-4 w-4 mr-2" /> {t("reseller.deviceStock.add")}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingDevice ? t("reseller.deviceStock.edit") : t("reseller.deviceStock.add")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="imei">ID *</Label>
                    <Input id="imei" name="imei" required defaultValue={editingDevice?.imei || ""} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="serial_number">{t("reseller.deviceStock.serialNumber")}</Label>
                    <Input id="serial_number" name="serial_number" defaultValue={editingDevice?.serial_number || ""} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("reseller.deviceStock.manufacturer")} *</Label>
                    <Select
                      name="manufacturer"
                      required
                      value={selectedManufacturer}
                      onValueChange={(v) => { setSelectedManufacturer(v); setSelectedModel(""); }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("reseller.deviceStock.manufacturer")} />
                      </SelectTrigger>
                      <SelectContent>
                        {MANUFACTURERS.map((m) => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t("reseller.deviceStock.model")} *</Label>
                    <Select
                      name="model"
                      required
                      value={selectedModel}
                      onValueChange={setSelectedModel}
                      disabled={!selectedManufacturer}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("reseller.deviceStock.model")} />
                      </SelectTrigger>
                      <SelectContent>
                        {(MANUFACTURER_MODELS[selectedManufacturer] ?? []).map((m) => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("reseller.deviceStock.simCard")}</Label>
                    {createNewSim ? (
                      <Button type="button" variant="outline" className="w-full justify-between font-normal" onClick={() => { setCreateNewSim(false); setNewSimIccid(""); setNewSimPhone(""); setSelectedSimId("none"); }}>
                        <span className="truncate">{t("reseller.deviceStock.createNewSim") || "Create new SIM"}</span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    ) : (
                      <Popover open={simOpen} onOpenChange={setSimOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" role="combobox" aria-expanded={simOpen} className="w-full justify-between font-normal">
                            <span className="truncate">
                              {selectedSimId === "none"
                                ? t("reseller.deviceStock.noSimCard")
                                : (() => { const s = simCards.find((x) => x.id === selectedSimId); return s ? `${s.iccid}${s.phone_number ? ` (${s.phone_number})` : ""}` : t("reseller.deviceStock.noSimCard"); })()}
                            </span>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                          <Command>
                            <CommandInput placeholder={t("reseller.deviceStock.searchSimCard") || "Search SIM..."} />
                            <CommandList>
                              <CommandEmpty>{t("reseller.deviceStock.noSimCardFound") || "No SIM found."}</CommandEmpty>
                              <CommandGroup>
                                <CommandItem value="none" onSelect={() => { setSelectedSimId("none"); setSimOpen(false); }}>
                                  <Check className={cn("mr-2 h-4 w-4", selectedSimId === "none" ? "opacity-100" : "opacity-0")} />
                                  {t("reseller.deviceStock.noSimCard")}
                                </CommandItem>
                                <CommandItem value="__create_new__" onSelect={() => { setCreateNewSim(true); setSelectedSimId("none"); setSimOpen(false); }}>
                                  <Plus className="mr-2 h-4 w-4" />
                                  {t("reseller.deviceStock.createNewSim") || "Create new SIM"}
                                </CommandItem>
                                {[...simCards].sort((a, b) => a.iccid.localeCompare(b.iccid)).map((s) => {
                                  const label = `${s.iccid}${s.phone_number ? ` (${s.phone_number})` : ""}`;
                                  return (
                                    <CommandItem key={s.id} value={label} onSelect={() => { setSelectedSimId(s.id); setSimOpen(false); }}>
                                      <Check className={cn("mr-2 h-4 w-4", selectedSimId === s.id ? "opacity-100" : "opacity-0")} />
                                      {label}
                                    </CommandItem>
                                  );
                                })}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="supplier">{t("reseller.deviceStock.supplier")}</Label>
                    <Input id="supplier" name="supplier" defaultValue={editingDevice?.supplier || ""} />
                  </div>
                </div>
                {createNewSim && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="new_sim_iccid">ICCID *</Label>
                      <Input id="new_sim_iccid" value={newSimIccid} onChange={(e) => setNewSimIccid(e.target.value)} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new_sim_phone">{t("reseller.simCard.phoneNumber") || "Phone Number"}</Label>
                      <Input id="new_sim_phone" value={newSimPhone} onChange={(e) => setNewSimPhone(e.target.value)} />
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="status">{t("reseller.deviceStock.status")}</Label>
                  <Select name="status" defaultValue={editingDevice?.status || "in_stock"}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_stock">{t("reseller.deviceStock.statuses.in_stock")}</SelectItem>
                      <SelectItem value="deployed">{t("reseller.deviceStock.statuses.deployed")}</SelectItem>
                      <SelectItem value="returned">{t("reseller.deviceStock.statuses.returned")}</SelectItem>
                      <SelectItem value="maintenance">{t("reseller.deviceStock.statuses.maintenance")}</SelectItem>
                      <SelectItem value="retired">{t("reseller.deviceStock.statuses.retired")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="purchase_date">{t("reseller.deviceStock.purchaseDate")}</Label>
                    <Input id="purchase_date" name="purchase_date" type="date" defaultValue={editingDevice?.purchase_date || ""} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="purchase_price">{t("reseller.deviceStock.purchasePrice")}</Label>
                    <Input id="purchase_price" name="purchase_price" type="number" step="0.01" defaultValue={editingDevice?.purchase_price || ""} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="warranty_expiry">{t("reseller.deviceStock.warrantyExpiry")}</Label>
                    <Input id="warranty_expiry" name="warranty_expiry" type="date" defaultValue={editingDevice?.warranty_expiry || ""} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">{t("reseller.deviceStock.notes")}</Label>
                  <Textarea id="notes" name="notes" defaultValue={editingDevice?.notes || ""} />
                </div>
                <Button type="submit" className="w-full" disabled={createMutation.isPending || updateMutation.isPending}>
                  {editingDevice ? t("common.update") : t("common.create")}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("reseller.deviceStock.searchPlaceholder")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }} >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={t("reseller.deviceStock.filterByStatus")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("common.all")}</SelectItem>
                  <SelectItem value="in_stock">{t("reseller.deviceStock.statuses.in_stock")}</SelectItem>
                  <SelectItem value="deployed">{t("reseller.deviceStock.statuses.deployed")}</SelectItem>
                  <SelectItem value="returned">{t("reseller.deviceStock.statuses.returned")}</SelectItem>
                  <SelectItem value="maintenance">{t("reseller.deviceStock.statuses.maintenance")}</SelectItem>
                  <SelectItem value="retired">{t("reseller.deviceStock.statuses.retired")}</SelectItem>
                </SelectContent>
              </Select>
              {(selectedIds.size > 0 || bulkDeleting) && (
                <div className="flex items-center gap-3">
                  <Button
                    variant="destructive"
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {bulkDeleteProgress
                      ? `${bulkDeleteProgress.done} / ${bulkDeleteProgress.total}`
                      : `${t("common.delete")} (${selectedIds.size})`}
                  </Button>
                  {bulkDeleteProgress && (
                    <div className="flex items-center gap-2 min-w-[200px]">
                      <div className="flex-1 bg-muted rounded-full h-2">
                        <div
                          className="bg-destructive h-2 rounded-full transition-all"
                          style={{ width: `${(bulkDeleteProgress.done / bulkDeleteProgress.total) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm text-muted-foreground whitespace-nowrap">
                        {Math.round((bulkDeleteProgress.done / bulkDeleteProgress.total) * 100)}%
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">{t("common.loading")}</div>
            ) : total === 0 ? (
              <div className="text-center py-8 text-muted-foreground">{t("reseller.deviceStock.noDevices")}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={devices.length > 0 && selectedIds.size === devices.length}
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                    {([
                      ["imei", "ID"],
                      ["serial_number", t("reseller.deviceStock.serialNumber")],
                      ["model", t("reseller.deviceStock.model")],
                      ["status", t("reseller.deviceStock.status")],
                    ] as const).map(([col, label]) => (
                      <TableHead key={col}>
                        <button className="flex items-center gap-1 hover:text-foreground" onClick={() => {
                          if (sortBy === col) setSortDir((d) => d === "asc" ? "desc" : "asc");
                          else { setSortBy(col); setSortDir("asc"); }
                          setPage(1);
                        }}>
                          {label}
                          {sortBy === col ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        </button>
                      </TableHead>
                    ))}
                    <TableHead>{t("reseller.deviceStock.location")}</TableHead>
                    <TableHead>{t("reseller.deviceStock.customer")}</TableHead>
                    <TableHead>{t("reseller.deviceStock.vehicle")}</TableHead>
                    <TableHead>{t("reseller.deviceStock.simCard")}</TableHead>
                    <TableHead className="w-[150px]">{t("reseller.common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {devices.map((device) => (
                    <TableRow key={device.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(device.id)}
                          onCheckedChange={() => toggleSelect(device.id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{device.imei}</TableCell>
                      <TableCell>{device.serial_number || "-"}</TableCell>
                      <TableCell>{[device.manufacturer, device.model].filter(Boolean).join(" ") || device.model}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[device.status]}>{t(`reseller.deviceStock.statuses.${device.status}`)}</Badge>
                      </TableCell>
                      <TableCell>{device.location?.name || "-"}</TableCell>
                      <TableCell>
                        {device.customer?.name ? (
                          <button
                            className="hover:underline text-sm text-left"
                            onClick={() => navigate(`/devices?search=${encodeURIComponent(device.customer!.name)}`)}
                          >
                            {device.customer.name}
                          </button>
                        ) : "-"}
                      </TableCell>
                      <TableCell>
                        {device.traccar_device_id && traccarDeviceMap.has(device.traccar_device_id) ? (
                          <button
                            className="hover:underline text-sm text-left"
                            onClick={() => navigate(`/devices?search=${device.traccar_device_id}`)}
                          >
                            {traccarDeviceMap.get(device.traccar_device_id!)}
                          </button>
                        ) : "-"}
                      </TableCell>
                      <TableCell>
                        {device.sim_card ? (
                          <button className="hover:underline text-sm text-left" onClick={() => navigate(`/sim-cards?search=${encodeURIComponent(device.sim_card!.iccid)}`)}>
                            {device.sim_card.phone_number || device.sim_card.iccid}
                          </button>
                        ) : "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" onClick={() => { setEditingDevice(device); setSelectedManufacturer(device.manufacturer || ""); setSelectedModel(device.model || ""); setSelectedSimId(device.sim_card_id || "none"); setIsOpen(true); }}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t("common.edit")}</TooltipContent>
                          </Tooltip>
                          {(device.status === "in_stock" || device.status === "returned") && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" onClick={() => { setSelectedDevice(device); setIsDeployOpen(true); }}>
                                  <Rocket className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>{t("reseller.deviceStock.deploy")}</TooltipContent>
                            </Tooltip>
                          )}
                          {device.status === "deployed" && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" onClick={() => { setSelectedDevice(device); setIsReturnOpen(true); }}>
                                  <Undo2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>{t("reseller.deviceStock.return")}</TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" onClick={() => { setHistoryDevice(device); setIsHistoryOpen(true); }}>
                                <History className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t("reseller.deviceStock.history")}</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" onClick={() => { if (confirm(t("reseller.common.deleteConfirm"))) deleteMutation.mutate(device.id); }}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t("common.delete")}</TooltipContent>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-1 pt-4">
                <span className="text-sm text-muted-foreground">
                  {t("common.showingRange", { from: (page - 1) * PAGE_SIZE + 1, to: Math.min(page * PAGE_SIZE, total), total })}
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => p - 1)} disabled={page === 1}>{t("common.previous")}</Button>
                  <span className="text-sm text-muted-foreground">{t("common.pageOf", { page, total: totalPages })}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages}>{t("common.next")}</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Deploy Dialog */}
        <Dialog open={isDeployOpen} onOpenChange={(open) => { setIsDeployOpen(open); if (!open) resetDeployState(); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("reseller.deviceStock.deployDevice")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleDeploySubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>{t("reseller.deviceStock.customer")} *</Label>
                <Popover open={deployCustomerOpen} onOpenChange={setDeployCustomerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" aria-expanded={deployCustomerOpen} className="w-full justify-between font-normal">
                      {deployCustomerId ? customers.find((c) => c.id === deployCustomerId)?.name : t("reseller.deviceStock.selectCustomer")}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder={t("reseller.deviceStock.selectCustomer")} />
                      <CommandList>
                        <CommandEmpty>{t("common.noResults")}</CommandEmpty>
                        <CommandGroup>
                          {[...customers].sort((a, b) => a.name.localeCompare(b.name)).map((c) => (
                            <CommandItem key={c.id} value={c.name} onSelect={() => { setDeployCustomerId(c.id); setDeployTraccarDeviceId(""); setDeployCustomerOpen(false); }}>
                              <Check className={cn("mr-2 h-4 w-4", deployCustomerId === c.id ? "opacity-100" : "opacity-0")} />
                              {c.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>{t("reseller.deviceStock.vehicle")} *</Label>
                <Popover open={deployVehicleOpen} onOpenChange={setDeployVehicleOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" aria-expanded={deployVehicleOpen} className="w-full justify-between font-normal" disabled={!deployCustomerId}>
                      {deployTraccarDeviceId ? customerVehicles.find((d) => String(d.id) === deployTraccarDeviceId)?.name : (deployCustomerId ? t("reseller.deviceStock.selectVehicle") : t("reseller.deviceStock.selectCustomerFirst"))}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder={t("reseller.deviceStock.selectVehicle")} />
                      <CommandList>
                        <CommandEmpty>{t("common.noResults")}</CommandEmpty>
                        <CommandGroup>
                          {[...customerVehicles].sort((a, b) => a.name.localeCompare(b.name)).map((d) => (
                            <CommandItem key={d.id} value={d.name} onSelect={() => { setDeployTraccarDeviceId(String(d.id)); setDeployVehicleOpen(false); }}>
                              <Check className={cn("mr-2 h-4 w-4", deployTraccarDeviceId === String(d.id) ? "opacity-100" : "opacity-0")} />
                              {d.name}
                            </CommandItem>
                          ))}
                          <CommandItem value="__create_new__" className="text-primary font-medium" onSelect={() => { setIsAddVehicleFromDeploy(true); setDeployVehicleOpen(false); }}>
                            + {t("reseller.vehicle.add")}
                          </CommandItem>
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <Button type="submit" className="w-full" disabled={isDeploying || !deployCustomerId || !deployTraccarDeviceId}>
                {isDeploying && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t("reseller.deviceStock.deploy")}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <AddVehicleDialog
          open={isAddVehicleFromDeploy}
          onOpenChange={(open) => { if (!open) setIsAddVehicleFromDeploy(false); }}
          onCreated={async (device) => {
            if (selectedCustomer?.traccar_user_id) {
              await linkDevice(selectedCustomer.traccar_user_id, device.id);
              queryClient.invalidateQueries({ queryKey: ["customer-vehicles", selectedCustomer.traccar_user_id] });
            }
            setDeployTraccarDeviceId(String(device.id));
          }}
        />

        {/* Return Dialog */}
        <Dialog open={isReturnOpen} onOpenChange={(open) => { setIsReturnOpen(open); if (!open) setSelectedDevice(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("reseller.deviceStock.returnDevice")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleReturnSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>{t("reseller.deviceStock.serialNumber")}</Label>
                <Input value={selectedDevice?.serial_number || ""} disabled />
              </div>
              <div className="space-y-2">
                <Label>{t("reseller.deviceStock.currentCustomer")}</Label>
                <Input value={selectedDevice?.customer?.name || "-"} disabled />
              </div>
              <div className="space-y-2">
                <Label htmlFor="return_reason">{t("reseller.deviceStock.returnReason")}</Label>
                <Textarea id="return_reason" name="return_reason" placeholder={t("reseller.deviceStock.returnReasonPlaceholder")} />
              </div>
              <Button type="submit" className="w-full" disabled={isReturning || returnMutation.isPending}>
                {(isReturning || returnMutation.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t("reseller.deviceStock.return")}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* History Dialog */}
        <Dialog open={isHistoryOpen} onOpenChange={(open) => { setIsHistoryOpen(open); if (!open) setHistoryDevice(null); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t("reseller.deviceStock.history")} - {historyDevice?.serial_number}</DialogTitle>
            </DialogHeader>
            <div className="max-h-[400px] overflow-y-auto">
              {historyLoading ? (
                <div className="text-center py-4 text-muted-foreground">{t("common.loading")}</div>
              ) : history.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">{t("reseller.deviceStock.noHistory")}</div>
              ) : (
                <div className="space-y-4">
                  {history.map((entry) => (
                    <div key={entry.id} className="border-l-2 border-muted pl-4 pb-4">
                      <div className="flex items-center gap-2">
                        <Badge variant={entry.action === "deployed" ? "default" : "secondary"}>
                          {t(`reseller.deviceStock.actions.${entry.action}`)}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(entry.created_at).toLocaleString()}
                        </span>
                      </div>
                      {entry.customer && (
                        <p className="text-sm mt-1">{t("reseller.deviceStock.customer")}: {entry.customer.name}</p>
                      )}
                      {entry.notes && (
                        <p className="text-sm text-muted-foreground mt-1">{entry.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
